import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, BookOpen, Award, Calendar, Target, Compass } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const StudyWithUsPage = () => {
  const programFeatures = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Field Methods Training",
      description: "Hands-on training in ethnographic research, data collection, and field research methodologies"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Expert Mentorship",
      description: "Work closely with experienced researchers and Highland Fellows on active research projects"
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: "PGCertR Pathway",
      description: "Complete your internship and earn a Postgraduate Certificate in Research (PGCertR)"
    },
    {
      icon: <Compass className="w-6 h-6" />,
      title: "Research Ethics",
      description: "Learn ethical research practices and community-engaged research methodologies"
    }
  ];

  const researchThemes = [
    {
      title: "Environmental Studies",
      description: "Climate change, biodiversity, and sustainable resource management in Highland Asia"
    },
    {
      title: "Medical Anthropology",
      description: "Indigenous healing systems, plural medicine, and health in climate-altered landscapes"
    },
    {
      title: "Cultural Heritage",
      description: "Oral histories, community archives, and intangible cultural heritage preservation"
    },
    {
      title: "Community Development",
      description: "Participatory research, citizen science, and collaborative knowledge production"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_institute-clone/artifacts/uy0foh7q_Study%20with%20Us%20Film%20Studies.jpg)'
          }}
        ></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Study With Us
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed mb-8">
              Internship Programme & Research Training
            </p>
            <p className="text-xl text-white max-w-3xl mx-auto">
              Our interns gain hands-on training in field methods, research ethics, and proposal development under expert mentorship.
            </p>
          </div>
        </div>
      </section>

      {/* Programme Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center" id="internship">Internship Programme</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The Highland Institute's Internship Programme offers a unique opportunity for aspiring researchers to engage 
                in community-based research in Highland Asia. Our programme combines intensive field training, theoretical 
                learning, and practical research experience under the guidance of experienced scholars and Fellows.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Interns work on active research projects, develop their own research proposals, and gain valuable experience 
                in ethnographic methods, research ethics, and collaborative knowledge production with local communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programme Features */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What You'll Learn</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {programFeatures.map((feature, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-lg">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Overview, Timeline & Eligibility */}
      <section className="py-16 bg-white" id="overview">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Overview, Timeline, and Eligibility</h2>
            
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Programme Duration</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                The internship typically runs for 8-12 weeks during the summer months (May-August), with opportunities 
                for extended engagement based on research needs and availability.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Eligibility</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Undergraduate students (final year) or recent graduates</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Strong interest in community-based research and Highland Asia</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Commitment to ethical research practices and community engagement</span>
                </li>
                <li className="flex items-start">
                  <ArrowRight className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">Basic research or fieldwork experience preferred but not required</span>
                </li>
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Application Timeline</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">January - February: Applications Open</p>
                    <p className="text-gray-600 text-sm">Submit your application with statement of interest and research proposal</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">March: Selection & Interviews</p>
                    <p className="text-gray-600 text-sm">Shortlisted candidates interviewed by research mentors</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">April: Offer & Pre-Departure Preparation</p>
                    <p className="text-gray-600 text-sm">Successful applicants receive offers and begin orientation</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-green-600 mt-1 mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900">May-August: Programme Duration</p>
                    <p className="text-gray-600 text-sm">Field research, workshops, and project completion</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PGCertR Pathway */}
      <section className="py-16 bg-gray-50" id="pgcertr">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Postgraduate Certificate in Research (PGCertR)</h2>
            <div className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 leading-relaxed mb-6">
                Interns who successfully complete the programme can earn a Postgraduate Certificate in Research (PGCertR), 
                a recognized qualification that demonstrates advanced research skills and methodological competence.
              </p>
              <div className="border-l-4 border-green-600 pl-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Certification Requirements</h3>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Complete 8-12 week internship programme</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Submit final research report or publication draft</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Present research findings at Institute seminar</span>
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="w-4 h-4 text-green-600 mt-1 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">Demonstrate ethical research practices and community engagement</span>
                  </li>
                </ul>
              </div>
              <p className="text-gray-700 leading-relaxed">
                The PGCertR is particularly valuable for those pursuing further graduate studies or careers in research, 
                international development, or community-based work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Research Themes */}
      <section className="py-16 bg-white" id="themes">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Research Themes & Projects</h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Interns work on projects aligned with the Institute's core research themes, contributing to ongoing 
              investigations while developing their own research skills and interests.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {researchThemes.map((theme, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-green-600">{theme.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{theme.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mentors & Faculty */}
      <section className="py-16 bg-gray-50" id="faculty">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Mentors & Faculty</h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Our interns work closely with experienced researchers, Highland Fellows, and visiting scholars who provide 
              guidance, mentorship, and expertise throughout the programme.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/people">
                <Button className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg">
                  Meet Our Staff
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/fellows">
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg">
                  Highland Fellows
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-green-700 text-white" id="certification">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Begin Your Research Journey?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Applications for the next internship programme open in January. Get in touch to learn more.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/summer-school">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Learn More
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudyWithUsPage;
